import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { User } from "@/auth/authSlice";

export interface PlatformSessionState {
  active: boolean;
  sessionId: string | null;
  tenantId: string | null;
  tenantName: string | null;
  expiresAtUtc: string | null;
  /** Stashed platform-owner token/user, restored on exit. */
  homeAccessToken: string | null;
  homeTokenExpiry: string | null;
  homeUser: User | null;
  /** Stashed platform-owner permission codes, restored on exit so the tenant Admin's codes
   *  don't linger in Redux after the session ends. */
  homePermissionCodes: string[] | null;
}

const initialState: PlatformSessionState = {
  active: false,
  sessionId: null,
  tenantId: null,
  tenantName: null,
  expiresAtUtc: null,
  homeAccessToken: null,
  homeTokenExpiry: null,
  homeUser: null,
  homePermissionCodes: null,
};

export interface EnterSupportSessionPayload {
  sessionId: string;
  tenantId: string;
  tenantName: string;
  expiresAtUtc: string;
  homeAccessToken: string;
  homeTokenExpiry: string;
  homeUser: User;
  homePermissionCodes: string[];
}

const platformSessionSlice = createSlice({
  name: "platformSession",
  initialState,
  reducers: {
    enterSupportSession: (state, action: PayloadAction<EnterSupportSessionPayload>) => {
      const {
        sessionId,
        tenantId,
        tenantName,
        expiresAtUtc,
        homeAccessToken,
        homeTokenExpiry,
        homeUser,
        homePermissionCodes,
      } = action.payload;
      state.active = true;
      state.sessionId = sessionId;
      state.tenantId = tenantId;
      state.tenantName = tenantName;
      state.expiresAtUtc = expiresAtUtc;
      state.homeAccessToken = homeAccessToken;
      state.homeTokenExpiry = homeTokenExpiry;
      state.homeUser = homeUser;
      state.homePermissionCodes = homePermissionCodes;
    },
    /**
     * Rehydrates the slice from the persisted auth envelope on app bootstrap. Distinct from
     * `enterSupportSession` because entering has side effects that must happen exactly once
     * per session (token swap, cache purge, audit-visible start call) — a reload is resuming
     * a session that is already running server-side, not starting a new one.
     */
    restoreSupportSession: (_state, action: PayloadAction<PlatformSessionState>) =>
      action.payload,
    exitSupportSession: () => initialState,
  },
});

export const { enterSupportSession, restoreSupportSession, exitSupportSession } =
  platformSessionSlice.actions;
export default platformSessionSlice.reducer;
